import React, { useState } from 'react';
import { Star } from 'lucide-react';
import { useTranslation } from '../context/TranslationContext';
import reviewText from '../data/reviewText';

var ACCENTS = {
  gold: { text: 'text-[#d4af37]', fill: 'fill-[#d4af37]', btn: 'bg-[#d4af37] text-black hover:bg-white', border: 'focus:border-[#d4af37]' },
  sky: { text: 'text-sky-400', fill: 'fill-sky-400', btn: 'bg-sky-500 text-black hover:bg-sky-300', border: 'focus:border-sky-400' },
  emerald: { text: 'text-emerald-400', fill: 'fill-emerald-400', btn: 'bg-emerald-500 text-black hover:bg-emerald-300', border: 'focus:border-emerald-400' }
};

// Shared review form — product pages and the forScore / MobileSheets review
// strips all post through this one component.
export function ReviewForm(props) {
  var langRef = useTranslation();
  var tx = reviewText[langRef.language] || reviewText.en;
  var a = ACCENTS[props.accent || 'gold'] || ACCENTS.gold;

  var formState = useState({ name: '', email: '', rating: 5, text: '' });
  var form = formState[0];
  var setForm = formState[1];
  var statusState = useState({ loading: false, message: '', error: false });
  var status = statusState[0];
  var setStatus = statusState[1];
  var hoverState = useState(0);
  var hover = hoverState[0];
  var setHover = hoverState[1];

  function set(key, value) {
    setForm(Object.assign({}, form, { [key]: value }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) {
      setStatus({ loading: false, message: tx.errName, error: true });
      return;
    }
    if (!form.email.trim()) {
      setStatus({ loading: false, message: tx.errEmail, error: true });
      return;
    }
    setStatus({ loading: true, message: '', error: false });
    fetch(process.env.REACT_APP_BACKEND_URL + '/api/reviews/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        product_slug: props.productSlug,
        name: form.name,
        email: form.email,
        rating: form.rating,
        text: form.text
      })
    })
      .then(function(res) { return res.json(); })
      .then(function(data) {
        if (data.success) {
          setStatus({ loading: false, message: tx.thanks, error: false });
          setForm({ name: '', email: '', rating: 5, text: '' });
          if (props.onSubmitted) props.onSubmitted();
        } else {
          setStatus({ loading: false, message: data.detail || tx.errFail, error: true });
        }
      })
      .catch(function() {
        setStatus({ loading: false, message: tx.errFail, error: true });
      });
  }

  var subtitle = props.subtitle != null
    ? props.subtitle
    : tx.writeSubtitle.replace('{name}', props.productName || '');

  return (
    <div className="bg-neutral-900 rounded-2xl p-6 sm:p-8 border border-white/5" data-testid="review-form">
      <h3 className="text-2xl font-bold text-white mb-2">{props.title || tx.writeTitle}</h3>
      <p className="text-neutral-500 text-sm mb-6">{subtitle}</p>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm text-neutral-400 mb-2">{tx.rating}</label>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map(function(starValue) {
              return (
                <button key={starValue} type="button"
                        data-testid={'review-star-' + starValue}
                        onClick={function() { set('rating', starValue); }}
                        onMouseEnter={function() { setHover(starValue); }}
                        onMouseLeave={function() { setHover(0); }}
                        className="p-1 transition-transform hover:scale-110">
                  <Star className={'w-8 h-8 transition-colors ' + ((hover || form.rating) >= starValue ? a.fill + ' ' + a.text : 'text-neutral-600')} />
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-neutral-400 mb-2">{tx.name} *</label>
            <input type="text" value={form.name} placeholder={tx.namePh}
                   data-testid="review-name-input"
                   onChange={function(e) { set('name', e.target.value); }}
                   className={'w-full bg-neutral-800 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-neutral-600 focus:outline-none ' + a.border} />
          </div>
          <div>
            <label className="block text-sm text-neutral-400 mb-2">{tx.email} *</label>
            <input type="email" value={form.email} placeholder={tx.emailPh}
                   data-testid="review-email-input"
                   onChange={function(e) { set('email', e.target.value); }}
                   className={'w-full bg-neutral-800 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-neutral-600 focus:outline-none ' + a.border} />
            <p className="text-neutral-600 text-xs mt-1">{tx.emailNote}</p>
          </div>
        </div>

        <div>
          <label className="block text-sm text-neutral-400 mb-2">{tx.text} *</label>
          <textarea value={form.text} rows={4} placeholder={tx.textPh}
                    data-testid="review-text-input"
                    onChange={function(e) { set('text', e.target.value); }}
                    className={'w-full bg-neutral-800 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-neutral-600 focus:outline-none resize-none ' + a.border} />
        </div>

        {status.message ? (
          <p data-testid="review-form-message"
             className={'text-sm ' + (status.error ? 'text-red-400' : 'text-green-400')}>{status.message}</p>
        ) : null}

        <button type="submit" disabled={status.loading} data-testid="review-submit-button"
                className={'w-full md:w-auto px-8 py-3 font-semibold rounded-full transition-all disabled:opacity-50 ' + a.btn}>
          {status.loading ? tx.submitting : tx.submit}
        </button>
      </form>
    </div>
  );
}

export default ReviewForm;
